import React from 'react';
import { Box } from '@mui/material';
import { FormSectionHeader } from 'modules/desking';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { AutocompleteField, FormInputSelect, InputField, SelectField } from 'components/form';
import { FinanceProgramFormValues, FormSectionProps } from 'modules/desking/types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Lead, StartDealRenderProps } from '../types';

type Props = FormSectionProps & StartDealRenderProps;

export const LeadSection = ({
  options,
  fetchLead,
  sectionName,
  isLeadLoading,
  isOptionsLoading,
  partnerLinksOptions,
  isAftermarketPartner,
  dealerLocationsOptions,
  isDealerLocationsLoading,
  handleFetchDealerLocations,
  isPartnerLinksOptionsLoading,
}: Props) => {
  const { control, setValue, getValues, resetField } = useFormContext<FinanceProgramFormValues>();

  const handleChangeContact = async (leadId: string) => {
    const lead = await fetchLead(leadId);

    if (!lead) return;

    Object.keys(getValues('contact')).forEach(key => {
      const path = `contact.${key}` as Path<FinanceProgramFormValues>;
      setValue(path, lead[key as keyof Lead]);
    });

    setValue('contact.fullName', lead.contact);
    setValue('contact.selectedContact', lead.id);
  };

  const handleResetSelectedContact = () => {
    if (getValues('contact.selectedContact')) {
      resetField('contact.selectedContact');
    }
  };

  return (
    <Box className="section">
      <FormSectionHeader name="Lead" sectionName={sectionName} />

      <Box className="sectionFields">
        <RenderWithCondition condition={isAftermarketPartner}>
          <Box className="row">
            <AutocompleteField
              control={control}
              name="contact.assignDealer"
              options={partnerLinksOptions}
              disabled={!partnerLinksOptions.length}
              isLoading={isPartnerLinksOptionsLoading}
              textFieldProps={{ label: 'Assign Dealer' }}
              extraAction={async nextValue => {
                resetField('contact.dealerLocation', { keepTouched: false });
                if (nextValue) await handleFetchDealerLocations(nextValue.dynamicsPartnerAccountId);
              }}
            />

            <SelectField
              size="medium"
              control={control}
              label="Dealer Location"
              name="contact.dealerLocation"
              options={dealerLocationsOptions}
              isLoading={isDealerLocationsLoading}
              disabled={!dealerLocationsOptions.length}
            />
          </Box>
        </RenderWithCondition>

        <Controller
          control={control}
          name="contact.selectedContact"
          render={({ field }) => (
            <FormInputSelect
              variant="standard"
              label="Select Lead"
              options={options}
              isLoading={isOptionsLoading}
              disabled={isLeadLoading || !options.length}
              {...field}
              onChange={handleChangeContact}
            />
          )}
        />

        <InputField
          control={control}
          label="Business Name"
          name="contact.businessName"
          extraAction={{ cb: handleResetSelectedContact }}
        />

        <Box className="row">
          <InputField
            label="Contact"
            control={control}
            name="contact.fullName"
            inputProps={{ required: true }}
            extraAction={{ cb: handleResetSelectedContact }}
          />

          <InputField
            label="Title"
            control={control}
            name="contact.title"
            extraAction={{ cb: handleResetSelectedContact }}
          />
        </Box>

        <Box className="row">
          <InputField
            label="Email"
            control={control}
            name="contact.email"
            extraAction={{ cb: handleResetSelectedContact }}
            inputProps={{ type: 'email', required: true }}
          />

          <InputField
            control={control}
            label="Phone Number"
            name="contact.phone"
            extraAction={{ cb: handleResetSelectedContact }}
            inputProps={{ type: 'tel', mask: '(999) 999-9999' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
